import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { chargingApi, type EndReason, type SessionOut, type SessionState } from '@/api/charging';
import { getMe, type MeOut } from '@/api/alianca';
import { referralApi } from '@/api/referral';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Display, Eyebrow, MoneyDisplay } from '@/components/ui/Typography';
import { AliancaReceiptCard } from '@/components/alianca/AliancaReceiptCard';
import { VacuumReceiptCard } from '@/components/amenities/VacuumReceiptCard';
import { PostChargeRating } from '@/components/feedback/PostChargeRating';
import { formatBRL, formatKwh } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';
import { walletApi, walletNumbers, type WalletOut } from '@/api/wallet';

// Chave do localStorage que marca uma sessão como já avaliada/dispensada,
// pra não reabrir o card de NPS ao voltar pro recibo.
const npsSeenKey = (sessionId: string) => `nps:${sessionId}`;

const TERMINAL_STATES: ReadonlySet<SessionState> = new Set([
  'completed',
  'failed',
  'cancelled'
]);

// Términos "normais" (você encerrou, limite atingido, charger parou sozinho):
// não geram a linha "Como terminou" — o sucesso já está claro no hero.
const NORMAL_END_REASONS: ReadonlySet<EndReason> = new Set([
  'user_stop',
  'limit_value_reached',
  'limit_energy_reached',
  'stop_transaction',
  'hub_ended'
]);

// Janela curta antes de liberar o "Tentar de novo" no recibo de Sem plug —
// dá um respiro pro charger voltar a Available e desencoraja a metralhadora
// de retries que infla o end_reason em prod.
const RETRY_COOLDOWN_SECONDS = 12;

// Start não confirmado (KI-040 do Hub): o carregador segue armado com a
// tentativa anterior por até ~60s e recusa qualquer retry nessa janela (WEG,
// 13 e 17/09). O backend segura o novo start no mesmo conector pelo mesmo
// tempo — o recibo só espelha, pra o botão não oferecer um "Tentar" que daria 409.
const UNCONFIRMED_HOLD_SECONDS = 60;

/** Recarga que nem começou: recibo dedicado, recuperável, sem cobrança. */
export type StartFailedReason =
  | 'charger_timeout_no_plug'
  | 'charger_start_unconfirmed'
  | 'charger_fault';

const START_FAILED_REASONS: ReadonlySet<EndReason> = new Set<StartFailedReason>([
  'charger_timeout_no_plug',
  'charger_start_unconfirmed',
  'charger_fault'
]);

/** Segundos até liberar o "Tentar de novo" — pura, testável sem browser. */
export function startFailedRetryWait(
  reason: StartFailedReason,
  startedAtIso: string,
  nowMs: number
): number {
  if (reason !== 'charger_start_unconfirmed') return RETRY_COOLDOWN_SECONDS;
  const elapsedS = (nowMs - Date.parse(startedAtIso)) / 1000;
  if (Number.isNaN(elapsedS)) return RETRY_COOLDOWN_SECONDS;
  return Math.max(0, Math.ceil(UNCONFIRMED_HOLD_SECONDS - elapsedS));
}

// Só motivos informativos/anômalos têm rótulo. Motivo sem rótulo aqui →
// linha omitida (nunca mostramos o código cru pro cliente).
const END_REASON_LABELS: Partial<Record<EndReason, string>> = {
  car_disconnected: 'Carro desconectado',
  hub_error: 'Erro de comunicação com o carregador',
  hub_timeout: 'Tempo esgotado no carregador',
  station_fault: 'Falha no carregador',
  manual_unlock: 'Liberada manualmente pelo suporte',
  charger_timeout_no_plug: 'O cabo não foi conectado a tempo',
  charger_start_unconfirmed: 'O carregador não confirmou o início',
  charger_fault: 'O carregador ou o carro recusou o início'
};

/** Qual card de cross-sell inline o recibo mostra — no máximo UM. */
export type ReceiptCrossSell = 'vacuum' | 'alianca' | null;

/**
 * Escolhe o ÚNICO card de cross-sell do recibo.
 *
 * O aspirador tem PRECEDÊNCIA porque o convite dele é perecível — só vale
 * enquanto o cliente ainda está fisicamente no pátio. O da Aliança é durável:
 * reaparece em todo recibo futuro e tem aba própria.
 *
 * Retorno único (em vez de dois booleanos independentes) é o que impede o
 * recibo de empilhar dois CTAs se alguém mexer num dos gates depois.
 */
export function pickCrossSell(args: {
  success: boolean;
  stationId?: string | null;
  vacuumFromBrl?: string | null;
  vacuumFromSeconds?: number | null;
  aliancaAvailable: boolean;
  energyKwh: number;
}): ReceiptCrossSell {
  const temAspirador =
    !!args.stationId &&
    args.vacuumFromBrl != null &&
    Number(args.vacuumFromBrl) > 0 &&
    !!args.vacuumFromSeconds;
  if (args.success && temAspirador) return 'vacuum';
  if (args.aliancaAvailable && args.energyKwh > 0) return 'alianca';
  return null;
}

export default function Receipt() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [showRating, setShowRating] = useState(false);
  const [aliancaMe, setAliancaMe] = useState<MeOut | null>(null);
  // Carteira ATUAL — a pendência do recibo é fato histórico da sessão, mas a
  // nota e o CTA falam no presente. Sem isto, quem já quitou reabre o recibo
  // antigo e lê "adicione saldo pra liberar novas recargas" (achado da revisão).
  const [walletNow, setWalletNow] = useState<WalletOut | null>(null);

  useEffect(() => {
    walletApi
      .get()
      .then(setWalletNow)
      .catch(() => {
        /* best-effort: sem a carteira, a nota some (conservador) */
      });
  }, []);

  // Estado da Aliança — fetch próprio, não bloqueia o recibo. Só decide se
  // mostramos o convite de adesão (não-membro). Falha → sem convite.
  useEffect(() => {
    getMe()
      .then(setAliancaMe)
      .catch(() => setAliancaMe(null));
  }, []);

  // Indique e Ganhe — só decide se a linha "Indique um amigo" aparece
  // (programa ligado). Falha/desligado → sem linha.
  const [referralEnabled, setReferralEnabled] = useState(false);
  useEffect(() => {
    referralApi
      .me()
      .then((r) => setReferralEnabled(!!r.enabled && !!r.code))
      .catch(() => setReferralEnabled(false));
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    chargingApi
      .get(sessionId)
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        // Card de NPS: só em recarga concluída e se o cliente ainda não
        // avaliou/dispensou esta sessão (guarda no localStorage).
        if (s.state === 'completed' && !localStorage.getItem(npsSeenKey(sessionId))) {
          setShowRating(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(friendlyMessage(err, 'Falha ao carregar recibo'));
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  function dismissRating() {
    if (sessionId) localStorage.setItem(npsSeenKey(sessionId), '1');
    setShowRating(false);
  }

  async function handleDownloadReceipt() {
    if (!sessionId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      if (Capacitor.isNativePlatform()) {
        // WebView do Capacitor não baixa `blob:` (sem handler de download):
        // navega pro link assinado e o Capacitor abre URL externa no
        // navegador do sistema, que baixa o PDF. O app fica onde está.
        window.location.assign(await chargingApi.receiptDownloadUrl(sessionId));
        return;
      }
      const blob = await chargingApi.downloadReceiptPdf(sessionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bow-recibo-${sessionId.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(friendlyMessage(err, 'Não foi possível baixar o recibo'));
    } finally {
      setDownloading(false);
    }
  }

  if (error) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        {error}
      </div>
    );
  }
  if (!session) {
    return <div className="h-64 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }

  // Recarga que nem começou ("sem plug", start não confirmado, carregador
  // recusou) não é recarga concluída nem erro alarmante: custo 0. Recibo
  // dedicado e recuperável, com retry direto pro mesmo conector — corta o
  // "retry storm" de quem voltava pra home e refazia o caminho inteiro.
  if (session.end_reason && START_FAILED_REASONS.has(session.end_reason)) {
    return (
      <StartFailedReceipt
        session={session}
        reason={session.end_reason as StartFailedReason}
      />
    );
  }

  const cost = Number(session.cost_brl);
  const success = session.state === 'completed';
  const tariff = Number(session.tariff_per_kwh_brl);
  const energy = Number(session.energy_kwh);

  // Quebra REAL da cobrança (Carteiras/T6) — só quando o GET trouxe os campos
  // novos (paid_brl/pending_brl). Sessões antigas seguem sem a seção.
  const paid = session.paid_brl != null ? Number(session.paid_brl) : null;
  const pending = session.pending_brl != null ? Number(session.pending_brl) : 0;
  const hasPending = pending > 0;
  // A dívida ainda existe AGORA? Só então a nota/CTA de aporte fazem sentido.
  const debtNow = walletNow ? walletNumbers(walletNow).debt : 0;
  const showPendingCall = hasPending && debtNow > 0;
  const paidWithBonus = session.pay_from === 'bonus';
  // Saldo sem pendência: linha só quando houve clamp (paid < cost) — no caso
  // normal seria redundante com o Total.
  const showBreakdown =
    paid != null && (paidWithBonus || hasPending || paid < cost);

  const crossSell = pickCrossSell({
    success,
    stationId: session.station_id,
    vacuumFromBrl: session.vacuum_from_brl,
    vacuumFromSeconds: session.vacuum_from_seconds,
    aliancaAvailable: !!aliancaMe?.adesao_disponivel,
    energyKwh: energy
  });

  // NSS (design/no-scroll/nss-charging.jsx · ReceiptNS): viewport fixa
  // (100dvh − BottomNav), ações ancoradas no rodapé, hero comprime em tela
  // baixa (círculo 70→58, título 34→28). O card de NPS (PostChargeRating) já
  // é bottom-sheet overlay — convive com o no-scroll sem empurrar nada.
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col px-5"
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        {/* Miolo — rola só com fonte de acessibilidade grande (§5.2) */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto [@media(max-height:740px)]:gap-3.5">
          {/* Hero horizontal — círculo + título lado a lado (feedback Saulo
              2026-07-02: eyebrow duplicava o título e a data/hora não agrega
              aqui — recibo PDF e Histórico têm). Libera ~150px de altura. */}
          <div className="flex flex-shrink-0 items-center gap-4 pt-2 [@media(max-height:740px)]:gap-3 [@media(max-height:740px)]:pt-0">
            <div
              className="flex h-[70px] w-[70px] flex-shrink-0 items-center justify-center rounded-full text-white [@media(max-height:740px)]:h-[58px] [@media(max-height:740px)]:w-[58px]"
              style={{
                background:
                  success ? 'linear-gradient(135deg, #00C072 0%, #31B700 100%)' : '#001B74',
                boxShadow: success
                  ? '0 12px 28px rgba(0,192,114,0.35)'
                  : '0 12px 28px rgba(0,27,116,0.25)'
              }}
            >
              {success ? <CheckIcon /> : <DotIcon />}
            </div>
            <Display
              size={30}
              className="leading-tight [@media(max-height:740px)]:!text-[26px]"
            >
              {success ? (
                <>
                  Recarga
                  <br />
                  concluída.
                </>
              ) : (
                `Sessão ${session.state}`
              )}
            </Display>
          </div>

          {/* Total card */}
          <Card className="flex flex-shrink-0 flex-col gap-3 p-5 [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:p-4">
            <div className="flex items-baseline justify-between border-b border-bow-royal/5 pb-3 [@media(max-height:740px)]:pb-2.5">
              <Eyebrow>Total</Eyebrow>
              <MoneyDisplay value={cost} size={32} />
            </div>
            <SummaryRow
              label={`Energia (${formatKwh(energy)})`}
              value={formatBRL(cost)}
            />
            <SummaryRow
              label="Tarifa"
              value={`R$ ${tariff.toFixed(2).replace('.', ',')}/kWh`}
            />
            {showBreakdown && paid != null && (
              <div className="mt-2 flex flex-col border-t border-bow-royal/5 pt-3">
                {paidWithBonus ? (
                  <SummaryRow
                    label="Pago com cashback"
                    labelIcon={<CashbackGlyph />}
                    value={formatBRL(paid)}
                  />
                ) : (
                  <>
                    <SummaryRow label="Pago com saldo" value={formatBRL(paid)} />
                    {hasPending && (
                      <SummaryRow
                        label="Ficou pendente"
                        value={formatBRL(pending)}
                        tone="red"
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {session.end_reason &&
              !NORMAL_END_REASONS.has(session.end_reason) &&
              END_REASON_LABELS[session.end_reason] && (
                <SummaryRow
                  label="Como terminou"
                  value={END_REASON_LABELS[session.end_reason]!}
                />
              )}
          </Card>

          {showPendingCall && (
            <p
              className="flex-shrink-0 px-1 text-xs leading-relaxed"
              style={{ color: 'rgba(0,27,116,0.55)' }}
            >
              Seu saldo ficou em{' '}
              <strong className="font-semibold" style={{ color: '#D5152B' }}>
                −{formatBRL(pending)}
              </strong>
              . Adicione saldo pra liberar novas recargas — o mínimo já vem
              preenchido.
            </p>
          )}

          {/* Cross-sell inline — a escolha é de `pickCrossSell`, que devolve
              no máximo UM. Não trocar por dois booleanos independentes. */}
          {crossSell === 'vacuum' && (
            <div className="flex-shrink-0">
              <VacuumReceiptCard
                stationId={session.station_id!}
                fromBrl={Number(session.vacuum_from_brl)}
                fromSeconds={session.vacuum_from_seconds!}
                courtesy={session.vacuum_courtesy === true}
              />
            </div>
          )}

          {/* Convite à Aliança — só pra não-membro, ancorado no kWh da sessão */}
          {crossSell === 'alianca' && (
            <div className="flex-shrink-0">
              <AliancaReceiptCard energyKwh={energy} />
            </div>
          )}

          {/* Indique e Ganhe — UMA linha discreta (NSS: não é card), só em
              recarga concluída com energia e com o programa ligado. Momento
              de maior satisfação do fluxo. Não toca o cross-sell acima nem o
              overlay de NPS. */}
          {success && energy > 0 && referralEnabled && (
            <Link
              to="/indique"
              className="flex-shrink-0 px-1 text-center text-xs font-semibold underline-offset-2 hover:underline"
              style={{ color: 'rgba(0,27,116,0.6)' }}
            >
              Gostou? Indique um amigo e ganhe cashback ›
            </Link>
          )}
        </div>

        {/* Rodapé ancorado — ações nunca saem da tela (NSS) */}
        <div className="flex flex-shrink-0 flex-col gap-2.5 pb-2.5 pt-2.5 [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:pt-2">
          {showPendingCall && (
            <Button
              variant="primary"
              onClick={() =>
                navigate('/wallet/recharge', {
                  state: { from: `/receipt/${session.id}` }
                })
              }
            >
              <PlusIcon /> Adicionar saldo
            </Button>
          )}
          {TERMINAL_STATES.has(session.state) && (
            <Button variant="secondary" onClick={handleDownloadReceipt} disabled={downloading}>
              {downloading ? 'Gerando recibo…' : 'Baixar recibo (PDF)'}
            </Button>
          )}
          {downloadError && (
            <p className="px-1 text-sm text-bow-red">{downloadError}</p>
          )}

          <Link
            to="/"
            className="inline-flex h-[54px] flex-shrink-0 items-center justify-center rounded-full bg-bow-royal px-6 text-base font-semibold text-bow-cream"
          >
            Voltar para o início
          </Link>
        </div>
      </div>

      {showRating && (
        <PostChargeRating
          sessionId={session.id}
          reviewUrls={session.external_review_urls}
          onClose={dismissRating}
        />
      )}
    </div>
  );
}

const START_FAILED_COPY: Record<
  StartFailedReason,
  { title: [string, string]; lead: string; tail: string }
> = {
  charger_timeout_no_plug: {
    title: ['Faltou', 'plugar o cabo.'],
    lead: 'O carregador esperou ~60s e cancelou sozinho.',
    tail: 'Da próxima, vá até o carro e plugue o cabo assim que tocar em Iniciar.'
  },
  // Não foi o cabo: o "sem plug" chegou em segundos (KI-040 do Hub).
  charger_start_unconfirmed: {
    title: ['O carregador não', 'confirmou o início.'],
    lead: 'Não foi o cabo: a confirmação do carregador não chegou e a tentativa caiu.',
    tail: 'Deixe o cabo plugado e toque em Tentar de novo quando o botão liberar.'
  },
  charger_fault: {
    title: ['O carregador', 'recusou o início.'],
    lead: 'O carregador ou o carro não aceitou começar a recarga.',
    tail: 'Tente de novo ou use o outro conector. Se repetir, fale com o suporte.'
  }
};

export function StartFailedReceipt({
  session,
  reason
}: {
  session: SessionOut;
  reason: StartFailedReason;
}) {
  // Contagem regressiva pro retry: desabilita o CTA e mostra "Aguarde Ns"
  // antes de liberar (no start não confirmado, até fechar o tempo em que o
  // carregador ainda segura a tentativa anterior).
  const [remaining, setRemaining] = useState(() =>
    startFailedRetryWait(reason, session.started_at, Date.now())
  );
  const copy = START_FAILED_COPY[reason];

  useEffect(() => {
    if (remaining <= 0) return;
    const t = window.setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [remaining]);

  const ready = remaining <= 0;
  const retryTo = `/configure/${session.point_id}/${session.connector_id}`;

  // Mesmo frame NSS do recibo normal: hero em cima, ações ancoradas embaixo.
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col px-5"
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [@media(max-height:740px)]:gap-3">
          <div className="flex flex-shrink-0 flex-col items-start gap-4 pt-2 [@media(max-height:740px)]:gap-3 [@media(max-height:740px)]:pt-0">
            <div
              className="flex h-[70px] w-[70px] items-center justify-center rounded-full text-bow-cream [@media(max-height:740px)]:h-[58px] [@media(max-height:740px)]:w-[58px]"
              style={{
                background: '#001B74',
                boxShadow: '0 12px 28px rgba(0,27,116,0.25)'
              }}
            >
              <DotIcon />
            </div>
            <div>
              <Eyebrow>Recarga não iniciada</Eyebrow>
              <Display
                size={34}
                className="mt-2 leading-tight [@media(max-height:740px)]:!text-[28px]"
              >
                {copy.title[0]}
                <br />
                {copy.title[1]}
              </Display>
              <p className="mt-3 text-sm leading-relaxed text-bow-royal/65">
                {copy.lead}{' '}
                <strong className="font-semibold text-bow-royal">Não cobramos nada.</strong>{' '}
                {copy.tail}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col gap-2.5 pb-2.5 pt-2.5 [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:pt-2">
          <Link
            to={ready ? retryTo : '#'}
            aria-disabled={!ready}
            onClick={(e) => {
              if (!ready) e.preventDefault();
            }}
            className={`inline-flex h-[54px] items-center justify-center rounded-full px-6 text-base font-semibold transition ${
              ready
                ? 'bg-bow-royal text-bow-cream'
                : 'cursor-not-allowed bg-bow-royal/40 text-bow-cream/70'
            }`}
          >
            {ready ? 'Tentar de novo' : `Aguarde ${remaining}s`}
          </Link>

          <Link
            to="/"
            className="inline-flex h-[54px] items-center justify-center rounded-full border border-bow-royal/15 bg-white px-6 text-base font-semibold text-bow-royal"
          >
            Voltar para o início
          </Link>
        </div>
      </div>
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  tone?: 'default' | 'green' | 'red';
  /** Glifo pequeno antes do label (ex.: $ da carteira de cashback). */
  labelIcon?: ReactNode;
}

function SummaryRow({ label, value, tone = 'default', labelIcon }: SummaryRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="inline-flex items-center gap-1.5 text-sm text-bow-royal/65">
        {labelIcon}
        {label}
      </span>
      <span
        className={`text-right text-sm font-semibold ${
          tone === 'green'
            ? 'text-bow-green'
            : tone === 'red'
              ? 'text-[#D5152B]'
              : 'text-bow-royal'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/** Identidade da carteira de cashback: $ sobre o gradiente roxo da marca. */
function CashbackGlyph() {
  return (
    <span
      aria-hidden
      className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white"
      style={{ background: 'linear-gradient(135deg,#835DD0,#C04CAE)' }}
    >
      $
    </span>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

function DotIcon() {
  return (
    <span className="text-3xl text-bow-cream">·</span>
  );
}
